import { useRef, useState } from 'react'
import { zipFolder, ZIP_WARN_BYTES } from '../utils/zipFolder'
import { dataApi } from '../api/client'

// ── 상수 ──────────────────────────────────────────────────────────────
const INPUT_TYPES = [
  { val: 'auto', label: '자동 감지 (파일 확장자 추론)' },
  { val: 'obj',  label: 'OBJ — Wavefront' },
  { val: 'fbx',  label: 'FBX — Autodesk FBX' },
  { val: 'gltf', label: 'glTF / GLB' },
  { val: 'dae',  label: 'Collada DAE' },
  { val: 'ifc',  label: 'IFC — BIM 데이터' },
  { val: 'las',  label: 'LAS / LAZ — Point Cloud' },
  { val: 'citygml', label: 'CityGML' },
  { val: 'shp',  label: 'Shapefile (2D→3D)' },
  { val: 'geojson', label: 'GeoJSON (2D→3D)' },
  { val: 'kml',  label: 'KML (Collada 인스턴스)' },
]

const OUTPUT_TYPES = [
  { val: 'b3dm', label: 'B3DM — Batched 3D Model (기본)' },
  { val: 'i3dm', label: 'I3DM — Instanced 3D Model' },
  { val: 'pnts', label: 'PNTS — Point Cloud Tiles' },
]

const CRS_OPTIONS = [
  { code: '5186', label: 'EPSG:5186 — Korea 2000 Central (TM 중부원점)' },
  { code: '5179', label: 'EPSG:5179 — Korea 2000 / UTM-K (GRS80)' },
  { code: '5174', label: 'EPSG:5174 — Korean 1985 Central (Bessel 중부)' },
  { code: '5187', label: 'EPSG:5187 — Korea 2000 East (TM 동부원점)' },
  { code: '32652', label: 'EPSG:32652 — UTM Zone 52N' },
  { code: '3857', label: 'EPSG:3857 — Web Mercator' },
  { code: '4326', label: 'EPSG:4326 — WGS 84 (위경도)' },
  { code: '4978', label: 'EPSG:4978 — WGS84 ECEF (지구중심 3D)' },
]

const CUSTOM_CRS = 'custom'

const ACCEPTED_EXTS = ['.obj', '.ply', '.fbx', '.las', '.laz', '.gltf', '.glb', '.dae', '.ifc', '.gml', '.shp', '.geojson', '.gpkg', '.kml', '.zip']

// ── 타입 ──────────────────────────────────────────────────────────────
interface Opts {
  inputType: string; outputType: string; tilesVersion: string
  quantize: boolean; recursive: boolean
  crs: string; customCrs: string; proj: string
  xOffset: string; yOffset: string; zOffset: string
  longitude: string; latitude: string
  minLod: number; maxLod: number
}

const DEFAULT_OPTS: Opts = {
  inputType: 'auto', outputType: 'b3dm', tilesVersion: '1.1',
  quantize: true, recursive: false,
  crs: '5186', customCrs: '', proj: '',
  xOffset: '', yOffset: '', zOffset: '',
  longitude: '', latitude: '',
  minLod: 0, maxLod: 3,
}

// ── 소규모 컴포넌트 ───────────────────────────────────────────────────
function SectionTitle({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="flex items-center gap-2 text-[11px] font-bold text-primary-700 uppercase tracking-wide pb-1.5 border-b border-primary-100 mb-3">
      {label}
      {hint && <span className="font-mono text-[10px] text-slate-400 normal-case tracking-normal ml-auto">{hint}</span>}
    </div>
  )
}

function CheckOpt({ checked, onChange, label, hint, badge, desc }: {
  checked: boolean; onChange: (v: boolean) => void
  label: string; hint?: string; badge?: string; desc: string
}) {
  return (
    <label
      className={[
        'flex gap-3 p-2.5 rounded-lg border cursor-pointer transition-all select-none',
        checked ? 'bg-primary-50 border-primary-200' : 'bg-slate-50 border-slate-200 hover:border-slate-300',
      ].join(' ')}
      onClick={() => onChange(!checked)}
    >
      <span className={[
        'w-4 h-4 rounded flex items-center justify-center shrink-0 mt-0.5 border transition-colors',
        checked ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-slate-300',
      ].join(' ')}>
        {checked && (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold text-slate-800">{label}</span>
          {badge && <span className="text-[9px] font-bold text-orange-800 bg-orange-100 px-1.5 py-0.5 rounded">{badge}</span>}
          {hint && <code className="text-[10px] text-slate-400 font-mono ml-auto">{hint}</code>}
        </div>
        <p className="text-[11px] text-slate-500 mt-0.5">{desc}</p>
      </div>
    </label>
  )
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────
interface Props {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  onSwitchToSimple: () => void
}

export default function AdvancedRegisterModal({ open, onClose, onSuccess, onSwitchToSimple }: Props) {
  const fileRef   = useRef<HTMLInputElement>(null)
  const folderRef = useRef<HTMLInputElement | null>(null)

  const [step,        setStep]        = useState(1)
  const [uploadMode,  setUploadMode]  = useState<'file' | 'folder'>('file')
  const [file,        setFile]        = useState<File | null>(null)
  const [folderFiles, setFolderFiles] = useState<File[] | null>(null)
  const [folderName,  setFolderName]  = useState('')
  const [isDragging,  setIsDragging]  = useState(false)
  const [name,        setName]        = useState('')
  const [description, setDescription] = useState('')
  const [opts,        setOpts]        = useState<Opts>(DEFAULT_OPTS)
  const [fileError,   setFileError]   = useState('')
  const [error,       setError]       = useState('')
  const [uploadPct,   setUploadPct]   = useState<number | null>(null)
  const [zipping,     setZipping]     = useState(false)
  const [zipPct,      setZipPct]      = useState(0)

  if (!open) return null

  const isBusy     = zipping || (uploadPct !== null && uploadPct < 100)
  const hasInput   = uploadMode === 'file' ? !!file : (folderFiles !== null && folderFiles.length > 0)
  const step1Valid = hasInput && name.trim().length > 0
  const crsIsCustom = opts.crs === CUSTOM_CRS
  const crsValid    = !crsIsCustom || /^\d+$/.test(opts.customCrs.trim())
  const effectiveCrs = crsIsCustom ? opts.customCrs.trim() : opts.crs

  const set = (patch: Partial<Opts>) => setOpts(p => ({ ...p, ...patch }))

  const pickFile = (f: File) => {
    if (!ACCEPTED_EXTS.some(ext => f.name.toLowerCase().endsWith(ext))) {
      setFileError('지원하지 않는 파일 형식입니다.'); return
    }
    setFile(f); setFileError('')
  }

  const pickFolder = (files: FileList) => {
    if (files.length === 0) return
    const arr = Array.from(files)
    const top = arr[0].webkitRelativePath.split('/')[0] || '폴더'
    setFolderFiles(arr)
    setFolderName(top)
    if (!name) setName(top)
    setFileError('')
  }

  const reset = () => {
    setStep(1); setUploadMode('file')
    setFile(null); setFolderFiles(null); setFolderName('')
    setName(''); setDescription('')
    setOpts(DEFAULT_OPTS); setFileError(''); setError('')
    setUploadPct(null); setZipping(false); setZipPct(0)
  }
  const handleClose = () => { reset(); onClose() }

  const handleSubmit = async () => {
    if (!name.trim() || !crsValid) return
    setError('')

    let uploadFile: File
    if (uploadMode === 'folder') {
      if (!folderFiles || folderFiles.length === 0) return
      const totalSize = folderFiles.reduce((s, f) => s + f.size, 0)
      if (totalSize > ZIP_WARN_BYTES) {
        setError(`폴더 크기(${(totalSize / 1e9).toFixed(1)} GB)가 2 GB를 초과합니다. 미리 ZIP으로 압축한 뒤 파일 업로드를 이용하면 더 안정적입니다.`)
      }
      setZipping(true); setZipPct(0)
      try {
        const blob = await zipFolder(folderFiles, setZipPct)
        uploadFile = new File([blob], `${folderName}.zip`, { type: 'application/zip' })
      } catch {
        setError('폴더 압축에 실패했습니다. 다시 시도해주세요.')
        setZipping(false)
        return
      }
      setZipping(false)
    } else {
      if (!file) return
      uploadFile = file
    }

    setUploadPct(0)
    const fd = new FormData()
    fd.append('file', uploadFile)
    fd.append('name', name.trim())
    if (description.trim()) fd.append('description', description.trim())
    fd.append('crs', effectiveCrs)
    fd.append('xOffset', opts.xOffset || '0')
    fd.append('yOffset', opts.yOffset || '0')
    fd.append('zOffset', opts.zOffset || '0')
    fd.append('inputType', opts.inputType)
    fd.append('outputType', opts.outputType)
    fd.append('tilesVersion', opts.tilesVersion)
    fd.append('quantize', String(opts.quantize))
    fd.append('recursive', String(opts.recursive))
    fd.append('minLod', String(opts.minLod))
    fd.append('maxLod', String(opts.maxLod))
    if (opts.proj.trim()) fd.append('proj', opts.proj.trim())
    if (opts.longitude.trim()) fd.append('longitude', opts.longitude.trim())
    if (opts.latitude.trim()) fd.append('latitude', opts.latitude.trim())
    try {
      await dataApi.upload(fd, setUploadPct)
      reset(); onSuccess()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? '업로드에 실패했습니다.')
      setUploadPct(null)
    }
  }

  // CLI 미리보기 빌드
  const buildCliPreview = () => {
    const inputName = uploadMode === 'folder'
      ? (folderName ? `${folderName}.zip` : '<folder.zip>')
      : (file?.name ?? '<input>')
    const parts = ['java -jar mago-3d-tiler.jar']
    parts.push(`  -i "${inputName}"`)
    parts.push(`  -o "/output/${name || 'dataset'}"`)
    if (opts.inputType !== 'auto') parts.push(`  -it ${opts.inputType}`)
    parts.push(`  -ot ${opts.outputType}`)
    parts.push(`  -tv ${opts.tilesVersion}`)
    if (opts.proj.trim()) parts.push(`  -p "${opts.proj.trim()}"`)
    else parts.push(`  -c ${effectiveCrs || '<EPSG>'}`)
    const ox = opts.xOffset || '0', oy = opts.yOffset || '0', oz = opts.zOffset || '0'
    if (ox !== '0' || oy !== '0' || oz !== '0') parts.push(`  -xo ${ox} -yo ${oy} -zo ${oz}`)
    if (opts.quantize) parts.push('  -qt')
    if (opts.recursive) parts.push('  -r')
    if (opts.minLod !== 0 || opts.maxLod !== 3) parts.push(`  -nl ${opts.minLod} -xl ${opts.maxLod}`)
    if (opts.longitude.trim() && opts.latitude.trim()) parts.push(`  -lon ${opts.longitude} -lat ${opts.latitude}`)
      
    return parts.join(' \\\n')
  }

  const STEPS = ['1. 파일 · 정보', '2. 변환 옵션', '3. 좌표계', '4. 미리보기']

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: 800, maxWidth: '95vw', maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 rounded-t-xl shrink-0"
          style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)' }}>
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-white">상세 옵션 등록</h2>
            <p className="text-[11px] text-white/75 font-mono">mago-3d-tiler · OGC 3D Tiles 1.1</p>
          </div>
          {!isBusy && (
            <button onClick={handleClose} className="bg-white/20 hover:bg-white/30 text-white rounded p-1.5 transition text-sm leading-none">✕</button>
          )}
        </div>

        {/* Step tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50 shrink-0">
          {STEPS.map((label, i) => {
            const n = i + 1
            return (
              <button key={n} onClick={() => setStep(n)}
                className={[
                  'flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors',
                  step === n
                    ? 'border-primary-600 text-primary-700 bg-white font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-700',
                ].join(' ')}>
                {label}
              </button>
            )
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded text-sm mb-4">{error}</div>}

          {/* ─ Step 1: 파일 · 정보 · 포맷 ─ */}
          {step === 1 && (
            <div className="space-y-5">
              <SectionTitle label="3D Mesh 파일"/>

              {/* 업로드 방식 토글 */}
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => { if (!isBusy) { setUploadMode('file'); setFolderFiles(null); setFolderName(''); setFileError('') } }}
                  className={`flex-1 py-2 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                    uploadMode === 'file' ? 'bg-primary-600 text-white' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  파일 업로드
                </button>
                <button
                  type="button"
                  onClick={() => { if (!isBusy) { setUploadMode('folder'); setFile(null); setFileError('') } }}
                  className={`flex-1 py-2 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                    uploadMode === 'folder' ? 'bg-primary-600 text-white' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                  </svg>
                  폴더 업로드
                </button>
              </div>

              {uploadMode === 'file' ? (
                <>
                  <div
                    onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={e => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) pickFile(f) }}
                    onClick={() => !isBusy && fileRef.current?.click()}
                    className={[
                      'border-2 border-dashed rounded-lg p-7 text-center transition-colors',
                      isDragging ? 'border-primary-400 bg-primary-50' : 'border-slate-200 bg-slate-50 hover:border-slate-300',
                      isBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    <div className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 mx-auto mb-2 flex items-center justify-center">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                      </svg>
                    </div>
                    {file ? (
                      <p className="text-sm font-semibold text-slate-700">{file.name} <span className="text-slate-400 font-normal text-xs">({(file.size / 1024 / 1024).toFixed(2)} MB)</span></p>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-slate-700">파일을 드래그하거나 <span className="text-primary-600">클릭하여 선택</span></p>
                        <p className="text-[10px] text-slate-400 mt-1">OBJ · FBX · GLTF · GLB · DAE · 3DS · IFC · LAS · LAZ · CityGML · SHP · GeoJSON · GPKG · KML · ZIP</p>
                      </>
                    )}
                    <input ref={fileRef} type="file" className="sr-only"
                      onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f); e.target.value = '' }}
                      disabled={isBusy}/>
                  </div>
                  {fileError && <p className="text-xs text-red-600">{fileError}</p>}
                </>
              ) : (
                <div
                  onClick={() => !isBusy && folderRef.current?.click()}
                  className={[
                    'border-2 border-dashed rounded-lg p-7 text-center transition-colors',
                    'border-slate-200 bg-slate-50 hover:border-slate-300',
                    isBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                  ].join(' ')}
                >
                  <div className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 mx-auto mb-2 flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                    </svg>
                  </div>
                  {folderFiles ? (
                    <>
                      <p className="text-sm font-semibold text-slate-700">{folderName}</p>
                      <p className="text-xs text-slate-400 mt-1">{folderFiles.length}개 파일 선택됨</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-slate-700">클릭하여 <span className="text-primary-600">폴더 선택</span></p>
                      <p className="text-[10px] text-slate-400 mt-1">폴더 내 모든 파일이 ZIP으로 묶여 업로드됩니다</p>
                    </>
                  )}
                  <input
                    ref={(el) => {
                      if (el) el.webkitdirectory = true
                      folderRef.current = el
                    }}
                    type="file"
                    className="sr-only"
                    multiple
                    onChange={e => { if (e.target.files) pickFolder(e.target.files); e.target.value = '' }}
                    disabled={isBusy}
                  />
                </div>
              )}

              <SectionTitle label="기본 정보"/>
              <div className="space-y-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">제목 <span className="text-red-500">*</span></label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="예: 강남구 빌딩 메시" className="input-field" disabled={isBusy}/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">설명</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
                    placeholder="데이터에 대한 설명" className="input-field resize-none" disabled={isBusy}/>
                </div>
              </div>

              <SectionTitle label="입출력 포맷"/>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">입력 포맷 <code className="font-mono text-slate-400 ml-1">-it</code></label>
                  <select value={opts.inputType} onChange={e => set({ inputType: e.target.value })} className="input-field">
                    {INPUT_TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">출력 포맷 <code className="font-mono text-slate-400 ml-1">-ot</code></label>
                  <select value={opts.outputType} onChange={e => set({ outputType: e.target.value })} className="input-field">
                    {OUTPUT_TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">3D Tiles 버전 <code className="font-mono text-slate-400 ml-1">-tv</code></label>
                <div className="flex gap-2">
                  {['1.0', '1.1'].map(v => (
                    <button key={v} onClick={() => set({ tilesVersion: v })}
                      className={[
                        'flex-1 py-2 rounded-md text-sm font-semibold border transition-colors',
                        opts.tilesVersion === v
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300',
                      ].join(' ')}>
                      v{v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─ Step 2: 변환 옵션 ─ */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <SectionTitle label="최적화"/>
                <div className="space-y-2">
                  <CheckOpt checked={opts.quantize} onChange={v => set({ quantize: v })} label="Mesh Quantization" hint="-qt" desc="KHR_mesh_quantization — 메시 양자화로 파일 크기 감소"/>
                  <CheckOpt checked={opts.recursive} onChange={v => set({ recursive: v })} label="재귀 탐색" hint="-r" desc="하위 디렉토리까지 깊이 탐색"/>
                </div>
              </div>

              <div>
                <SectionTitle label="LOD 설정"/>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">최소 LOD 깊이 <code className="font-mono text-slate-400 ml-1">-nl</code></label>
                    <input type="number" min={0} max={22} value={opts.minLod} onChange={e => set({ minLod: Number(e.target.value) })} className="input-field"/>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">최대 LOD 깊이 <code className="font-mono text-slate-400 ml-1">-xl</code></label>
                    <input type="number" min={0} max={22} value={opts.maxLod} onChange={e => set({ maxLod: Number(e.target.value) })} className="input-field"/>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─ Step 3: 좌표계 ─ */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <SectionTitle label="좌표계 (CRS)"/>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">EPSG 코드 <code className="font-mono text-slate-400 ml-1">-c</code></label>
                    <select value={opts.crs} onChange={e => set({ crs: e.target.value })} className="input-field">
                      {CRS_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                      <option value={CUSTOM_CRS}>직접 입력 (EPSG 코드)</option>
                    </select>
                    {crsIsCustom && (
                      <div className="mt-2">
                        <input
                          type="text" inputMode="numeric" pattern="[0-9]*"
                          value={opts.customCrs}
                          onChange={e => set({ customCrs: e.target.value.replace(/[^0-9]/g, '') })}
                          placeholder="EPSG 코드 입력 (예: 5181)"
                          className="input-field"
                        />
                        {opts.customCrs.trim().length > 0 && !crsValid && (
                          <p className="text-xs text-red-600 mt-1">숫자로 된 EPSG 코드를 입력해주세요.</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      Proj4 파라미터 <code className="font-mono text-slate-400 ml-1">-p</code>
                      <span className="text-slate-400 font-normal ml-1">(입력 시 EPSG보다 우선 적용)</span>
                    </label>
                    <input value={opts.proj} onChange={e => set({ proj: e.target.value })}
                      placeholder="+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 ..."
                      className="input-field font-mono text-xs"/>
                  </div>
                </div>
              </div>

              <div>
                <SectionTitle label="좌표 오프셋"/>
                <div className="grid grid-cols-3 gap-3">
                  {(['xOffset', 'yOffset', 'zOffset'] as const).map(k => (
                    <div key={k}>
                      <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                        {k === 'xOffset' ? 'X' : k === 'yOffset' ? 'Y' : 'Z'} Offset
                        <code className="font-mono text-slate-400 ml-1">-{k === 'xOffset' ? 'xo' : k === 'yOffset' ? 'yo' : 'zo'}</code>
                      </label>
                      <input type="number" step="any" value={opts[k]} onChange={e => set({ [k]: e.target.value } as Partial<Opts>)}
                        placeholder="0" className="input-field"/>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <SectionTitle label="원점 좌표 (로컬 데이터용)"/>
                <p className="text-[11px] text-slate-500 mb-3">좌표계 정보가 없는 로컬 기준 데이터일 때 지리 좌표 원점을 직접 지정합니다.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">경도 (Longitude) <code className="font-mono text-slate-400 ml-1">-lon</code></label>
                    <input type="number" step="any" value={opts.longitude} onChange={e => set({ longitude: e.target.value })}
                      placeholder="예: 127.0275" className="input-field"/>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">위도 (Latitude) <code className="font-mono text-slate-400 ml-1">-lat</code></label>
                    <input type="number" step="any" value={opts.latitude} onChange={e => set({ latitude: e.target.value })}
                      placeholder="예: 37.4988" className="input-field"/>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─ Step 4: CLI 미리보기 ─ */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <SectionTitle label="CLI 명령 미리보기"/>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide mb-2">mago-3d-tiler command</p>
                  <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap break-all leading-relaxed">{buildCliPreview()}</pre>
                </div>
              </div>

              {zipping && (
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>폴더 묶는 중... ({folderFiles?.length ?? 0}개 파일)</span>
                    <span>{zipPct}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div className="bg-primary-400 h-1.5 rounded-full transition-all duration-100" style={{ width: `${zipPct}%` }}/>
                  </div>
                </div>
              )}

              {uploadPct !== null && (
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>{uploadPct < 100 ? '서버 업로드 중...' : '업로드 완료'}</span>
                    <span>{uploadPct}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5">
                    <div className="bg-primary-600 h-1.5 rounded-full transition-all duration-200" style={{ width: `${uploadPct}%` }}/>
                  </div>
                  {uploadPct === 100 && (
                    <p className="text-[11px] text-slate-400 mt-1">서버에서 변환 작업을 시작했습니다. 완료 후 이메일이 발송됩니다.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-400">Step {step} / 4</span>
            <button onClick={onSwitchToSimple} disabled={isBusy}
              className="text-[11px] text-green-600 font-semibold hover:underline disabled:opacity-50 flex items-center gap-1">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              간편 등록으로 전환
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={handleClose} disabled={isBusy} className="btn-secondary disabled:opacity-50">취소</button>
            {step > 1 && (
              <button onClick={() => setStep(s => s - 1)} disabled={isBusy} className="btn-secondary disabled:opacity-50">이전</button>
            )}
            {step < 4 ? (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={(step === 1 && !step1Valid) || (step === 3 && !crsValid)}
                className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                다음
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!step1Valid || !crsValid || isBusy}
                className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {zipping ? `묶는 중... ${zipPct}%` : isBusy ? '업로드 중...' : '변환 시작'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
